import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { Icons, MUI, Unicons, Theme } from '../../styles';

// Material-UI Pickers
import { DateTimePicker, KeyboardDateTimePicker } from '@material-ui/pickers';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import moment from 'moment';
import MomentUtils from '@date-io/moment';
import 'moment/locale/en-gb';
import 'moment/locale/es-mx';
import 'moment/locale/pt';

// TextField DatePicker
const MuiTextField = ({ ...props }) => {
  return (
    <MUI.TextField
      {...props}
      InputProps={{ endAdornment: (<Unicons.Calendar size="20" />) }}
    />
  );
};

// TextFieldIcon DatePicker
const MuiTextFieldIcon = ({ ...props }) => {
  return (
    <MUI.TextFieldIcon
      {...props}
      InputProps={{ startAdornment: (<Icons.FilterList />), endAdornment: (<Unicons.Calendar size="20" style={{ minHeight: '20px', minWidth: '20px' }} />) }}
    />
  );
};

// Formats
// dd/mm/yyyy -> lu/09/2020
// DD/mm/yyyy -> 21/09/2020
// LL -> 21 de septiembre de 2020
// dddd LL -> viernes 21 de septiembre de 2020

class LocalizedUtils extends MomentUtils {
  getDatePickerHeaderText(date) {
    return moment(date).format('LL');
  }
}

const MuiDateTimePicker = (props) => {
  const lsLanguage = JSON.parse(localStorage.getItem('ls.ty-Language'));
  const lang = lsLanguage ? lsLanguage.language : 'es';
  const locale = {
    en: 'en-gb',
    es: 'es-mx',
    pt: 'pt'
  };

  // Set moment locale
  moment.locale(locale[lang]);

  const materialTheme = createTheme({
    overrides: {
      MuiPickersToolbar: {
        toolbar: {
          height: '70px',
          backgroundColor: Theme.colors.greyDark,
          '& h3': {
            fontFamily: 'Bryant Pro Bold',
            fontSize: Theme.typography.heading1,
            fontWeight: 'normal',
            letterSpacing: 'normal',
            whiteSpace: 'normal',
          },
          '& h4': {
            fontFamily: 'Bryant Pro Bold',
            fontSize: Theme.typography.heading4,
            fontWeight: 'normal',
            letterSpacing: 'normal',
            whiteSpace: 'normal',
          },
          '& h6': {
            fontFamily: 'Bryant Pro Regular',
            fontSize: Theme.typography.heading6,
            fontWeight: 'normal',
            letterSpacing: '0.0175em',
            whiteSpace: 'normal',
          },
        }
      },
      MuiPickerDTTabs: {
        tabs: {
          color: Theme.colors.greyDark,
          backgroundColor: Theme.colors.greyLight,
        }
      },
      MuiPickersCalendarHeader: {
        iconButton: {
          padding: '8px',
          color: Theme.colors.greyDark,
          '& svg': {
            fontSize: '20px'
          },
        },
        transitionContainer: {
          color: Theme.colors.greyDark,
          '& p': {
            fontFamily: 'Bryant Pro Regular',
            fontSize: Theme.typography.bodyText,
            fontWeight: 'normal',
            letterSpacing: '0.0175em',
            whiteSpace: 'normal',
          }
        },
        dayLabel: {
          color: Theme.colors.greyMedium,
          fontFamily: 'Bryant Pro Regular',
          fontSize: Theme.typography.smallText,
          fontWeight: 'normal',
          letterSpacing: '0.0175em',
          whiteSpace: 'normal',
        },
      },
      MuiPickersDay: {
        day: { // Days in calendar
          color: Theme.colors.greyDark,
          '& p': {
            fontFamily: 'Bryant Pro Regular',
            fontSize: Theme.typography.bodyText,
            fontWeight: 'normal',
            letterSpacing: '0.0175em',
            whiteSpace: 'normal',
          }
        },
        daySelected: { // Calendar circle
          backgroundColor: Theme.colors.blueDark,
          '&:hover': {
            backgroundColor: Theme.colors.blueDark,
          }
        },
        // dayDisabled: {},
        current: { // Current day
          color: Theme.colors.blueDark,
        },
      },
      MuiPickersYear: {
        root: {
          color: Theme.colors.greyDark,
          fontFamily: 'Bryant Pro Regular',
          fontSize: Theme.typography.bodyText,
          fontWeight: 'normal',
          letterSpacing: '0.0175em',
          whiteSpace: 'normal',

        },
        yearSelected: {
          color: Theme.colors.blueDark,
          fontFamily: 'Bryant Pro Medium',
        }
      },
      MuiPickersMonth: {
        root: {
          color: Theme.colors.greyDark,
          fontFamily: 'Bryant Pro Regular',
          fontSize: Theme.typography.bodyText,
          fontWeight: 'normal',
          letterSpacing: '0.0175em',
          whiteSpace: 'normal',

        },
        monthSelected: {
          color: Theme.colors.blueDark,
          fontFamily: 'Bryant Pro Medium',
        }
      },
      MuiPickersClock: {
        pin: {
          backgroundColor: Theme.colors.blueDark
        },
      },
      MuiPickersClockPointer: {
        pointer: {
          backgroundColor: Theme.colors.blueDark
        },
        noPoint: {
          backgroundColor: Theme.colors.blueDark
        },
        thumb: {
          borderColor: Theme.colors.blueDark
        }
      },
      MuiPickersClockNumber: {
        clockNumber: {
          color: Theme.colors.greyDark,
          fontFamily: 'Bryant Pro Regular',
          fontSize: Theme.typography.bodyText,
          fontWeight: 'normal',
          letterSpacing: '0.0175em',
          whiteSpace: 'normal',
        },
      }
      // MuiDialogActions: {
      //   root: { }
      // },
      // MuiPickersModal: {
      //   dialogAction: {
      //     color: CURRENT_THEME.main,
      //     backgroundColor: CURRENT_THEME
      //   },
      // },
    }
  });

  const {
    allowKeyboard,
    mask,
    isFilter,
    ...restProps
  } = props;

  // 
  return (
    <ThemeProvider theme={materialTheme}>
      <MuiPickersUtilsProvider utils={LocalizedUtils} locale={locale[lang]} moment={moment}>
        {!allowKeyboard ? (
          <DateTimePicker
            {...restProps}
            autoOk
            variant="inline"
            mask={mask ? mask : '__/__/____'}
            leftArrowIcon={<Icons.ArrowLeft />}
            rightArrowIcon={<Icons.ArrowRight />}
            dateRangeIcon={<Unicons.Calendar size="20" />}
            timeIcon={<Icons.Time />}
            TextFieldComponent={!isFilter ? MuiTextField : MuiTextFieldIcon}
          />
        ) : (
          <KeyboardDateTimePicker
            {...restProps}
            autoOk
            variant="inline"
            mask={mask ? mask : '__/__/____'}
            leftArrowIcon={<Icons.ArrowLeft />}
            rightArrowIcon={<Icons.ArrowRight />}
            dateRangeIcon={<Unicons.Calendar size="20" />}
            timeIcon={<Icons.Time />}
            TextFieldComponent={MUI.TextField}
          />
        )}
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  );
};

MuiDateTimePicker.propTypes = {
  // Boolean to change DatePicker to KeyboardDatePicker.
  allowKeyboard: PropTypes.bool,
  // Mask to keyboard.
  mask: PropTypes.string,
  // Boolean to show filter icon in TextField.
  isFilter: PropTypes.bool
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

/* KeyboardDateTimePicker Styles */
export const StyledDateTimePicker = styled(MuiDateTimePicker)`
  .MuiIconButton-root {
    padding: 8px;
    right: -8px;
    color: ${(props) => props.theme.colors.greyDark};
  }
`;